import {
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { config } from "@/config";
import { notifyFlexLogout } from "@/hooks/useFlexAppAuth";
import { isFlexApp } from "@/hooks/useIsFlexApp";
import { posthogReset } from "@/lib/posthog";
import {
	fetchAllPartners,
	fetchCurrentAgent,
	fetchCurrentPartner,
	fetchPartnerChoices,
	fetchPartnerForAgent,
	fetchUserDetails,
	fetchUserDetailsSafe,
	logout,
	updateAgentPause,
	verifyAgentAccess,
} from "./api";
import { getPreferredPartnerId, removePreferredPartnerId } from "./token";
import type { Agent, Partner, PartnerResponse, User } from "./types";

export const userQueryOptions = queryOptions<User>({
	queryKey: ["user"],
	queryFn: fetchUserDetails,
	staleTime: 5 * 60 * 1000,
	gcTime: 30 * 60 * 1000,
	retry: false,
});

/**
 * Safe user query that returns null instead of throwing on 401.
 * Use this for pages that need to check auth status without errors.
 */
export const userQueryOptionsSafe = queryOptions<User | null>({
	queryKey: ["user", "safe"],
	queryFn: fetchUserDetailsSafe,
	staleTime: 5 * 60 * 1000,
	gcTime: 30 * 60 * 1000,
	retry: false,
});

export const agentQueryOptions = queryOptions<Agent>({
	queryKey: ["currentAgent"],
	queryFn: fetchCurrentAgent,
	staleTime: 5 * 60 * 1000,
	gcTime: 30 * 60 * 1000,
	retry: false,
});

/**
 * Query options for verifying agent access (used in agent flow)
 */
export const agentVerificationQueryOptions = queryOptions<Agent>({
	queryKey: ["agent", "verification"],
	queryFn: verifyAgentAccess,
	staleTime: 5 * 60 * 1000,
	gcTime: 30 * 60 * 1000,
	retry: false,
});

/**
 * Query options factory for fetching partner data in agent access flow
 */
export const agentPartnerQueryOptions = (partnerId: string) =>
	queryOptions<Partner>({
		queryKey: ["partner", "agent-access", partnerId],
		queryFn: () => fetchPartnerForAgent(partnerId),
		staleTime: 0,
		gcTime: 30 * 60 * 1000,
		retry: false,
		enabled: !!partnerId,
	});

export const allPartnersQueryOptions = queryOptions<PartnerResponse>({
	queryKey: ["partners", "all", getPreferredPartnerId()],
	queryFn: fetchAllPartners,
	staleTime: 0, // Always refetch (cache=false behavior)
	gcTime: 30 * 60 * 1000,
	retry: false,
});

/**
 * All of the user's partner accounts, ignoring the preferred_partner cookie.
 * Used by the account picker so every account stays reachable.
 */
export const partnerChoicesQueryOptions = queryOptions<PartnerResponse>({
	queryKey: ["partners", "choices"],
	queryFn: fetchPartnerChoices,
	staleTime: 0,
	gcTime: 30 * 60 * 1000,
	retry: false,
});

export const partnerQueryOptions = queryOptions<Partner | null>({
	queryKey: ["currentPartner", getPreferredPartnerId()],
	queryFn: fetchCurrentPartner,
	staleTime: 0,
	gcTime: 30 * 60 * 1000,
	retry: false,
});

/**
 * Check if current domain is the partner subdomain (partner.*)
 */
function isPartnerSubdomain(): boolean {
	if (typeof window === "undefined") return false;
	return window.location.hostname.startsWith("partner.");
}

/**
 * Get the logout redirect URL - stays on partner subdomain if accessed from there
 */
export function getLogoutUrl(): string {
	if (isPartnerSubdomain()) {
		// Stay on partner subdomain
		return `${window.location.origin}/partner`;
	}
	const baseUrl = config.urls.webServiceUrl.replace(/\/+$/, "");
	return `${baseUrl}/partner`;
}

export const logoutMutationOptions = {
	mutationFn: logout,
	onSuccess: () => {
		// Reset PostHog identity
		posthogReset();
		// Clear preferred_partner cookie (for multi-partner accounts)
		removePreferredPartnerId();
		// Notify Flex native app about logout
		if (isFlexApp()) notifyFlexLogout();
		// Redirect to WEB_SERVICE_URL/partner
		window.location.href = getLogoutUrl();
	},
	onError: (error: Error) => {
		console.error("Logout failed:", error);
		// Even on error, try to redirect to partner page
		window.location.href = getLogoutUrl();
	},
};

export function useLogout() {
	return useMutation(logoutMutationOptions);
}

export function useToggleLeadsPause() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ agentId, pause }: { agentId: number; pause: boolean }) =>
			updateAgentPause(agentId, pause),
		onSuccess: (_, { pause }) => {
			queryClient.invalidateQueries({ queryKey: ["currentAgent"] });
			toast.success(pause ? "Session inactive" : "Session active", {
				description: pause
					? "Lead assignment has been paused until you resume your session."
					: "Pipeline enabled. Lead assignment resumed.",
			});
		},
	});
}

/**
 * Get the external login URL
 */
export function getLoginUrl(): string {
	if (typeof window !== "undefined") {
		const envLoginUrl = import.meta.env.VITE_LOGIN_URL;
		if (envLoginUrl) {
			return envLoginUrl;
		}
		// Use base service URL + /partner
		return `${config.urls.baseServiceUrl}/partner`;
	}
	return "https://amberstudent.com/partner";
}

/**
 * Get partner_id from URL query params (for agent access flow)
 */
export function getPartnerIdFromUrl(): string | null {
	if (typeof window !== "undefined") {
		const searchParams = new URLSearchParams(window.location.search);
		return searchParams.get("partner_id");
	}
	return null;
}

/**
 * Check if current access is via agent flow (partner_id in URL)
 */
export function isAgentAccessFlow(): boolean {
	return !!getPartnerIdFromUrl();
}
